import { inject, Injectable } from '@angular/core';
import { MockService } from '@mock/mock.service';
import { IPosition } from '@app/core/interfaces/position.interface';
import { MarkersService } from '@mock/utils/markers.service';
import { correlationsMockData } from '@mock/data/correlations-mock.data';
import { ICorrelation } from '@app/api/interfaces/correlation.interface';
import { IComment, IUpdateCommentRequest } from '@app/api/interfaces/comment.interface';
import { CorrelationMode } from '@app/api/enums/correlation-mode.enum';
import {
  getRandomBoolean,
  getRandomDateTime,
  getRandomInt,
  getRandomItem,
  getRandomStr,
} from '@app/shared/utils/random';
import { enumToArray } from '@app/shared/utils/enumerator';
import { CorrelationType } from '@app/core/enums/correlation-type.enum';
import { LoggerDeploymentsDataMockService } from '@mock/data-services/logger-deployments-data-mock.service';
import { IListFilter } from '@app/api/interfaces/common/list-filter';
import { delay, Observable } from 'rxjs';
import { IApiResponse } from '@app/api/interfaces/common/api-response.interface';
import { IPaginationMeta } from '@app/api/interfaces/common/pagination-meta.interface';
import { CommonStateStatus } from '@app/core/enums/common-state-status.enum';
import dayjs from 'dayjs';
import { CoordinateSystemsMockService } from '@mock/data-services/coordinate-systems-mock.service';
import { convertStringInToSumOfAllCharacters } from '@app/shared/utils/string';

@Injectable({ providedIn: 'root' })
export class CorrelationsMockService extends MockService {
  private markersService = inject(MarkersService);
  private loggerDeploymentsData = inject(LoggerDeploymentsDataMockService).loggerDeploymentsData;
  private coordinateSystemsMockService = inject(CoordinateSystemsMockService);

  public correlations: ICorrelation[] = [];

  private correlationsPositions: IPosition[] = [];
  private id = 1;

  constructor() {
    super();

    if (this.isMockDataGenerated()) {
      this.correlationsPositions = this.markersService.generateMarkersPositions(this.dataGeneratedCount.correlations);
      this.correlations = this.generateCorrelations();
    } else {
      this.correlations = correlationsMockData;
    }
  }

  public getAll(
    projectId: string,
    filter: IListFilter = {}
  ): Observable<IApiResponse<ICorrelation[], IPaginationMeta>> {
    const loggerDeploymentsData = this.isMockDataGenerated()
      ? this.loggerDeploymentsData
      : this.loggerDeploymentsData.filter(
          loggerDeploymentData =>
            loggerDeploymentData.projectId === projectId &&
            (!filter['areaId'] || filter['areaId'] === loggerDeploymentData.areaId)
        );

    const loggerDeploymentsDataMap = loggerDeploymentsData.reduce<Record<string, boolean>>(
      (acc, loggerDeploymentData) => {
        acc[loggerDeploymentData.deploymentId] = true;
        return acc;
      },
      {}
    );

    let correlations = this.isMockDataGenerated()
      ? this.correlations
      : this.correlations.filter(
          correlation =>
            loggerDeploymentsDataMap[correlation.logger1DeploymentId] ||
            loggerDeploymentsDataMap[correlation.logger2DeploymentId]
        );

    const asOfDate = filter['asOfDate'];

    if (typeof asOfDate === 'string' && asOfDate) {
      switch (convertStringInToSumOfAllCharacters(asOfDate) % 3) {
        // 1 - all data
        case 0:
          break;
        // 2 - half of the data
        case 1:
          correlations = correlations.slice(0, Math.floor(correlations.length / 2));
          break;
        // 3 - empty array
        case 2:
          correlations = [];
          break;
      }
    }

    return this.mockResponse(
      'CORRELATIONS:getALL',
      {
        data: correlations,
        meta: {
          pagination: { count: correlations.length, total: correlations.length, offset: 0, limit: correlations.length },
        },
      },
      arguments
    ).pipe(delay(this.requestDelay));
  }

  public getOne(
    mode: CorrelationMode,
    projectId: string,
    correlationId: string
  ): Observable<IApiResponse<ICorrelation, object>> {
    const correlation = this.correlations.find(item => item.id === correlationId);

    return this.mockResponse(
      'CORRELATIONS:getOne',
      {
        data: correlation as ICorrelation,
        meta: {},
      },
      arguments
    ).pipe(delay(this.requestDelay));
  }

  public updateComment(
    mode: CorrelationMode,
    projectId: string,
    correlationId: string,
    body: IUpdateCommentRequest
  ): Observable<IApiResponse<IComment, object>> {
    const correlation = this.correlations.find(item => item.id === correlationId);

    const comment: IComment = {
      content: body.content,
      author: 'Julian Mercer',
      modifiedAt: dayjs().toISOString(),
    };

    if (correlation) {
      const index = this.correlations.indexOf(correlation);
      this.correlations.splice(index, 1, { ...correlation, comment });
    }

    return this.mockResponse(
      'CORRELATIONS:updateComment',
      {
        data: comment,
        meta: {},
      },
      arguments
    ).pipe(delay(this.requestDelay));
  }

  private generateCorrelations(): ICorrelation[] {
    return this.correlationsPositions.map(position => {
      const correlationType = getRandomItem(enumToArray(CorrelationType));
      const comment = getRandomInt(0, 10) > 3 ? 'some comment' : undefined;
      const identifier = this.id++;
      // A correlation lives in the project of the loggers it links.
      const logger1 = getRandomItem(this.loggerDeploymentsData);

      return {
        id: 'correlation_gen' + identifier,
        quality: getRandomInt(0, 10) > 1 ? getRandomInt(1, 100) : undefined,
        qualityStatus: getRandomItem(enumToArray(CommonStateStatus)),
        correlationType,
        suppressed: getRandomBoolean(),
        logger1DeploymentId: logger1.deploymentId,
        logger2DeploymentId: getRandomItem(this.loggerDeploymentsData).deploymentId,
        soundSignalStartAt:
          getRandomInt(0, 10) > 7 ? dayjs('2025-03-12').add(getRandomInt(3, 4), 'hour').toISOString() : undefined,
        distanceL1: getRandomInt(0, 10) > 7 ? getRandomInt(100, 1000) : undefined,
        distanceL2: getRandomInt(0, 10) > 7 ? getRandomInt(100, 1000) : undefined,
        centerDistance: getRandomInt(0, 10) > 7 ? getRandomInt(100, 1000) : undefined,
        pipeLength: getRandomInt(0, 10) > 7 ? getRandomInt(1000, 2000) : undefined,
        pipeProvided: getRandomInt(0, 10) > 7 ? getRandomBoolean() : undefined,
        customCoordinates:
          getRandomInt(0, 10) > 7
            ? this.coordinateSystemsMockService.generateCustomCoordinates(logger1.projectId, identifier)
            : getRandomItem([undefined, null]),
        coordinates: position,
        comment: comment
          ? {
              content: comment,
              author: getRandomInt(0, 10) > 7 ? getRandomStr(getRandomInt(5, 50)) : undefined,
              modifiedAt: getRandomInt(0, 10) > 7 ? getRandomDateTime(100).toISOString() : undefined,
            }
          : undefined,
        averagingInterval:
          getRandomInt(0, 10) > 7
            ? {
                startAt: getRandomDateTime(getRandomInt(1, 50)).toISOString(),
                endAt: getRandomDateTime(1).toISOString(),
              }
            : getRandomInt(0, 10) > 7
              ? null
              : undefined,
      };
    });
  }
}
